export function parameterKey(parameters: Record<string, unknown>, name: string): string | undefined {
  return Object.keys(parameters).find(key => key.toLowerCase() === name.toLowerCase());
}

/** Shallow, case-insensitive presence merge; every explicit JSON value remains an override. */
export function mergeProcessParameters(base: Record<string, unknown>, overrides: Record<string, unknown>): Record<string, unknown> {
  const result = structuredClone(base);
  for (const [key, value] of Object.entries(overrides)) {
    const previous = parameterKey(result, key);
    if (previous !== undefined) delete result[previous];
    Object.defineProperty(result, key, { value: structuredClone(value), enumerable: true, writable: true, configurable: true });
  }
  return result;
}

export function withoutParameter(parameters: Record<string, unknown>, name: string): Record<string, unknown> {
  const result = { ...parameters };
  const previous = parameterKey(result, name);
  if (previous !== undefined) delete result[previous];
  return result;
}

export function parseParameterDefaults(json: string):
  { ok: true; parameters: Record<string, unknown> } | { ok: false; error: string } {
  try {
    const value: unknown = JSON.parse(json);
    if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error();
    if (hasDuplicateTopLevelKeys(json)) {
      return { ok: false, error: 'PROCESS_DEFAULTS_DUPLICATE_KEYS' };
    }
    return { ok: true, parameters: value as Record<string, unknown> };
  } catch {
    return { ok: false, error: 'Process parameters must be a valid JSON object' };
  }
}

/** The input is already valid JSON; inspect raw object keys before duplicate properties are collapsed. */
function hasDuplicateTopLevelKeys(json: string): boolean {
  const keys = new Set<string>();
  let depth = 0;
  for (let index = 0; index < json.length; index++) {
    const character = json[index];
    if (character === '{' || character === '[') {
      depth++;
    } else if (character === '}' || character === ']') {
      depth--;
    } else if (character === '"') {
      const start = index;
      index++;
      while (index < json.length && json[index] !== '"') {
        index += json[index] === '\\' ? 2 : 1;
      }
      let next = index + 1;
      while (next < json.length && /\s/.test(json[next])) next++;
      if (depth === 1 && json[next] === ':') {
        const key = (JSON.parse(json.slice(start, index + 1)) as string).toLowerCase();
        if (keys.has(key)) return true;
        keys.add(key);
      }
    }
  }
  return false;
}
